import type {
  FlowDocumentTypes,
  PoaDocumentTypes,
  ProductId,
} from './new-flow-wizard.state';

const POA_DOC_TYPES = new Set([
  'INVOICE',
  'BILL',
  'BANK',
  'LEASE',
  'OFFICIAL',
  'PAYSLIP',
  'INSURANCE',
]);
const IDV_DOC_TYPES = new Set([
  'ID',
  'PASSPORT',
  'RESIDENCE',
  'DRIVINGLICENSE',
]);
const CARD_DOC_TYPES = new Set(['CREDITCARD', 'CARD']);

type ParsedDocumentCheckOption = {
  docs: string[];
  providers: string;
};

export function normalizeCheckOptions(checkOptions: unknown): string[] {
  if (Array.isArray(checkOptions)) {
    return checkOptions.filter((opt): opt is string => typeof opt === 'string');
  }
  if (typeof checkOptions === 'string' && checkOptions.trim()) {
    return [checkOptions];
  }
  return [];
}

function parseDocumentCheckOption(
  opt: string,
): ParsedDocumentCheckOption | null {
  if (!opt.startsWith('document:')) return null;
  const match = opt.match(/^document:([^:]+):(.+)$/);
  if (!match) return null;

  return {
    docs: match[1]
      .split(',')
      .map(doc => doc.trim().toUpperCase())
      .filter(Boolean),
    providers: match[2].trim(),
  };
}

/**
 * POA is detected from POA document types (or legacy staple provider).
 * Do not treat `incode+sonio` alone as POA — that routing is POA-specific.
 */
export function isPoaCheckOption(opt: string): boolean {
  const parsed = parseDocumentCheckOption(opt);
  if (!parsed) return false;

  if (parsed.docs.some(doc => POA_DOC_TYPES.has(doc))) return true;

  const providers = parsed.providers.toLowerCase();
  return providers.includes('staple');
}

/**
 * IDV is detected from identity document types (or plain incode-only routing).
 * An option may also be POA if it mixes document types.
 */
export function isIdvCheckOption(opt: string): boolean {
  const parsed = parseDocumentCheckOption(opt);
  if (!parsed) return false;

  if (parsed.docs.some(doc => IDV_DOC_TYPES.has(doc))) return true;

  // Legacy IDV-only: document:ID,PASSPORT:incode (no POA docs, no sonio)
  if (parsed.docs.some(doc => POA_DOC_TYPES.has(doc))) return false;
  const providers = parsed.providers.toLowerCase();
  return providers === 'incode' || providers.startsWith('incode,');
}

export function isCardCheckOption(opt: string): boolean {
  const parsed = parseDocumentCheckOption(opt);
  if (!parsed) return false;
  return parsed.docs.some(doc => CARD_DOC_TYPES.has(doc));
}

export function detectProductsFromCheckOptions(
  checkOptions: unknown,
): ProductId[] {
  const opts = normalizeCheckOptions(checkOptions);

  // Card is exclusive — if present, it is the only product
  if (opts.some(isCardCheckOption)) return ['card'];

  const products: ProductId[] = [];
  if (opts.some(isIdvCheckOption)) products.push('idv');
  if (opts.some(isPoaCheckOption)) products.push('poa');

  return products;
}

export function filterDocsForProduct(
  docs: string[],
  productId: ProductId,
): string[] {
  if (productId === 'idv') {
    return docs.filter(d => IDV_DOC_TYPES.has(d.toUpperCase()));
  }
  if (productId === 'poa') {
    return docs.filter(d => POA_DOC_TYPES.has(d.toUpperCase()));
  }
  if (productId === 'card') {
    return docs.filter(d => CARD_DOC_TYPES.has(d.toUpperCase()));
  }
  return docs;
}

export function parseIdvDocumentTypesFromCheckOptions(
  checkOptions: unknown,
): Partial<FlowDocumentTypes> | null {
  const opts = normalizeCheckOptions(checkOptions).filter(isIdvCheckOption);
  if (!opts.length) return null;

  const docs = [
    ...new Set(
      opts.flatMap(opt => {
        const parsed = parseDocumentCheckOption(opt);
        return parsed ? filterDocsForProduct(parsed.docs, 'idv') : [];
      }),
    ),
  ];

  return {
    id: docs.includes('ID'),
    passport: docs.includes('PASSPORT'),
    residence: docs.includes('RESIDENCE'),
    drivingLicense: docs.includes('DRIVINGLICENSE'),
  };
}

export function parsePoaDocumentTypesFromCheckOptions(
  checkOptions: unknown,
): Partial<PoaDocumentTypes> | null {
  const opts = normalizeCheckOptions(checkOptions).filter(isPoaCheckOption);
  if (!opts.length) return null;

  const docs = [
    ...new Set(
      opts.flatMap(opt => {
        const parsed = parseDocumentCheckOption(opt);
        return parsed ? filterDocsForProduct(parsed.docs, 'poa') : [];
      }),
    ),
  ];

  return {
    invoice: docs.includes('INVOICE'),
    bill: docs.includes('BILL'),
    bank: docs.includes('BANK'),
  };
}
